import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Panel, Ref, Amount } from '@promanage/ui';
import { formatPeriod } from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { FinancialNav } from '../features/ledger/FinancialNav';
import {
  newIdempotencyKey,
  toPostInput,
  type AccountOption,
  type DraftTransaction,
} from '../features/ledger/batch';
import { TransactionFields } from '../features/ledger/TransactionFields';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { needsStepUp, trpc } from '../lib/trpc';
import { today } from '../lib/today';

export const Route = createFileRoute('/financial/new')({
  component: NewTransactionPage,
});

/**
 * Post one transaction by hand, immediately.
 *
 * The single-item counterpart to `/financial/entry`. An operator posting one correction should not
 * have to queue it and then process the queue, and an operator working through a bank statement
 * should not have to confirm twenty times — so both exist, and both use the same
 * `TransactionFields`, so the two can differ in what they do and never in what they accept.
 *
 * Everything that decides whether this is allowed already existed before the screen did: the
 * role and step-up gate, the domain rules, the period routing, and five deferred triggers that
 * refuse it at COMMIT whatever anything above them believes. This screen's job is to make the
 * refusals legible and get the entries right before spending a round trip.
 */
function NewTransactionPage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const accounts = useQuery(trpc.ledger.accounts.queryOptions(undefined, { staleTime: 30_000 }));
  const periods = useQuery(trpc.ledger.periods.queryOptions(undefined, { staleTime: 30_000 }));

  const [posted, setPosted] = useState<{
    refSeq: number;
    amountCents: string;
    amountFormatted: string;
    periodStartsOn: string;
    lateItem: boolean;
    journals: { id: string; ref: string; refSeq: number }[];
  } | null>(null);
  // Bumped after a post so the form remounts empty rather than being cleared field by field.
  const [formKey, setFormKey] = useState(0);

  /*
   * ONE KEY PER ATTEMPT AT ONE TRANSACTION, and the lifetime is the whole point.
   *
   * This form has the same hole the queue had: post, lose the response, and the operator is
   * looking at an error with their entry still on screen and an obvious button to press. Holding
   * the key in state means pressing it again carries the same one, so the server returns what it
   * already wrote instead of posting a near-identical second transaction.
   *
   * Reset only by `Post another`, which is the one action that means "a different event now".
   */
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const post = useMutation(
    trpc.ledger.post.mutationOptions({
      meta: { errorTitle: 'Could not post that transaction' },
      onSuccess(result) {
        unsaved.saved();
        setPosted(result);
        // Balances, both lists and possibly a newly-opened period all just changed.
        invalidateAccounts(queryClient);
        void queryClient.invalidateQueries({ queryKey: trpc.ledger.transactions.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.ledger.journals.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.ledger.periods.queryKey() });
      },
      onError(error) {
        // Fifteen minutes have passed since the last code. Not something to explain in a red box
        // on this form — send them to the prompt and let them come back.
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  const accountList: AccountOption[] = accounts.data ?? [];

  const submit = (draft: Omit<DraftTransaction, 'key' | 'idempotencyKey' | 'refusal'>) => {
    const input = toPostInput({ ...draft, key: 'one', idempotencyKey, refusal: null });
    // `draftIssues` already refuses anything unpostable; this is what stands in for a `!` on a
    // money path, which is the compiler being told to be quiet about an amount.
    if (input === null) return;
    post.mutate(input);
  };

  if (posted) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Transaction posted" subtitle="It is in the ledger and cannot be edited" />

        <Panel className="mt-4 max-w-[34rem] p-5">
          {/*
           * The TRANSACTION ref leads. It is the number to write down, quote on the phone and
           * search for; the journal ref beneath it is the accounting the entry produced. Every
           * journal belongs to a transaction, including a one-journal manual entry like this.
           */}
          <p className="flex flex-wrap items-center gap-2 text-[0.9375rem] font-semibold">
            <Ref entity="transaction" sequence={posted.refSeq} tone="strong" />
            <Amount value={BigInt(posted.amountCents)} currency tone="neutral" />
            <span className="font-normal text-ink-muted">
              posted to {formatPeriod(posted.periodStartsOn)}
            </span>
          </p>

          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[0.75rem] text-ink-subtle">
            <span>{posted.journals.length === 1 ? 'Journal' : 'Journals'}</span>
            {posted.journals.map((journal) => (
              <Ref key={journal.id} entity="journal" sequence={journal.refSeq} />
            ))}
          </p>

          {posted.lateItem ? (
            <p className="mt-3 rounded-md bg-warn-soft px-3 py-2 text-[0.8125rem] text-warn">
              The month this journal is dated into has been closed, so it posted to{' '}
              {formatPeriod(posted.periodStartsOn)} instead and is flagged as a prior-period
              adjustment. Its date is unchanged.
            </p>
          ) : null}

          <p className="mt-3 text-[0.8125rem] text-ink-muted">
            Financial rows are never edited or deleted. If this is wrong, the correction is a
            reversing journal that points back at it.
          </p>

          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                setPosted(null);
                setFormKey((n) => n + 1);
                // A new event, so a new key. Reusing this one would make the next transaction
                // a "replay" of the last and post nothing at all.
                setIdempotencyKey(newIdempotencyKey());
              }}
            >
              Post another
            </Button>
            <Link to="/financial">
              <Button size="sm" variant="secondary">
                Back to the ledger
              </Button>
            </Link>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New transaction"
        subtitle="One event, and the journal that records it"
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/financial/entry">
              <Button size="sm" variant="secondary">
                Enter several
              </Button>
            </Link>
            <Link to="/financial">
              <Button size="sm" variant="secondary">
                Cancel
              </Button>
            </Link>
          </div>
        }
      />
      <FinancialNav />

      <div onChange={unsaved.touch}>
        <TransactionFields
          key={formKey}
          accounts={accountList}
          periods={periods.data}
          today={today()}
          submitLabel="Post transaction"
          busy={post.isPending}
          onSubmit={submit}
        />
      </div>
    </div>
  );
}
